// client/src/components/payment/checkout/SquareCheckoutSection.jsx
//
// Square-specific half of UnifiedCheckout. Loads the Square Web Payments
// SDK on-demand (so Stripe-only flows don't pay the bundle cost) and
// renders a card-input container. On submit, calls card.tokenize() and
// hands the resulting nonce back to the parent via onSuccess({ sourceToken })
// — the server is the one that calls Square's createPayment.

import React, { useEffect, useRef, useState } from 'react';

// Cache loader promises so we don't inject the script tag multiple times.
const SDK_PROMISES = {};

function loadSquareSdk(environment) {
  const src = environment === 'sandbox'
    ? 'https://sandbox.web.squarecdn.com/v1/square.js'
    : 'https://web.squarecdn.com/v1/square.js';
  if (SDK_PROMISES[src]) return SDK_PROMISES[src];

  SDK_PROMISES[src] = new Promise((resolve, reject) => {
    if (typeof window !== 'undefined' && window.Square) {
      resolve(window.Square);
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      if (window.Square) resolve(window.Square);
      else reject(new Error('Square SDK loaded but window.Square missing'));
    };
    script.onerror = () => reject(new Error('Failed to load Square Web Payments SDK'));
    document.head.appendChild(script);
  });
  return SDK_PROMISES[src];
}

export default function SquareCheckoutSection({
  sdkConfig, amountLabel, onSuccess, onError, busy, setBusy, submitButtonStyle,
}) {
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let cardInstance = null;

    (async () => {
      try {
        if (!sdkConfig?.applicationId || !sdkConfig?.locationId) {
          throw new Error('Square sdkConfig missing applicationId/locationId');
        }
        const Square = await loadSquareSdk(sdkConfig.environment || 'production');
        if (cancelled) return;
        const payments = Square.payments(sdkConfig.applicationId, sdkConfig.locationId);
        cardInstance = await payments.card();
        if (cancelled) return;
        await cardInstance.attach(containerRef.current);
        cardRef.current = cardInstance;
        setLoading(false);
      } catch (err) {
        console.error('[Square SDK init] error:', err);
        if (!cancelled) {
          setError(err.message || 'Could not initialize Square checkout');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      try { cardInstance && cardInstance.destroy && cardInstance.destroy(); } catch (_) { /* ignore */ }
    };
  }, [sdkConfig?.applicationId, sdkConfig?.locationId, sdkConfig?.environment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cardRef.current || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await cardRef.current.tokenize();
      if (result.status === 'OK' && result.token) {
        onSuccess({ sourceToken: result.token });
      } else {
        const msg = result.errors?.map((er) => er.message).join('; ')
          || result.status
          || 'Tokenization failed';
        setError(msg);
        setBusy(false);
      }
    } catch (err) {
      onError(err?.message || 'Unexpected Square error');
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {loading && <div style={{ color: '#aaa', marginBottom: 12 }}>Loading Square checkout…</div>}
      <div ref={containerRef} style={{ minHeight: 90 }} />
      {error && (
        <div style={{ color: '#dc2626', marginTop: 12, fontSize: 14 }}>{error}</div>
      )}
      <button
        type="submit"
        disabled={loading || busy || !!error}
        style={submitButtonStyle(loading || busy)}
      >
        {busy ? 'Processing…' : `Pay ${amountLabel}`}
      </button>
    </form>
  );
}
